/**
 * Inventory ageing. Days are counted from the billing date to "today" in the
 * organisation's timezone, and every surface in the product — table chips, report
 * buckets, the 60-day alert — reads these definitions rather than hard-coding cutoffs.
 */

/**
 * Seven bands, seven tones.
 *
 * The tone is what the operator actually reads — the chip's colour tells them
 * whether to act before the label does — so every band needs its own. The three
 * oldest shared one tone until Aug 2026, which made a vehicle standing over a
 * year look exactly like one at ninety-five days, and those are not remotely
 * the same problem.
 *
 * The first two bands are deliberately *neutral* rather than green: new stock
 * is not an achievement to colour in, and tinting it spends attention that
 * belongs at the other end of the ramp. Heat starts at 31 days.
 */
export const AGEING_BUCKETS = [
  { key: 'D0_15', label: '0–15 days', short: '0–15', min: 0, max: 15, tone: 'fresh' },
  { key: 'D16_30', label: '16–30 days', short: '16–30', min: 16, max: 30, tone: 'settled' },
  { key: 'D31_60', label: '31–60 days', short: '31–60', min: 31, max: 60, tone: 'watch' },
  { key: 'D61_90', label: '61–90 days', short: '61–90', min: 61, max: 90, tone: 'warn' },
  // The slow tail, split at the marks the business actually argues about:
  // three months to six, six to a year, and the units past a full year.
  { key: 'D91_180', label: '91 days – 6 months', short: '≤6 m', min: 91, max: 180, tone: 'stale' },
  { key: 'D181_365', label: '6 months – 1 year', short: '≤1 y', min: 181, max: 365, tone: 'critical' },
  { key: 'D365_PLUS', label: 'Over 1 year', short: '1 y+', min: 366, max: null, tone: 'severe' },
] as const;

export type AgeingBucketKey = (typeof AGEING_BUCKETS)[number]['key'];
export type AgeingTone = (typeof AGEING_BUCKETS)[number]['tone'];

/** Which band a vehicle's days-held falls in. */
export function ageingBucket(days: number | null | undefined): AgeingBucketKey | null {
  if (days == null || Number.isNaN(days) || days < 0) return null;
  for (const b of AGEING_BUCKETS) {
    if (days >= b.min && (b.max === null || days <= b.max)) return b.key;
  }
  return 'D365_PLUS';
}

export function ageingTone(days: number | null | undefined): AgeingTone | null {
  const key = ageingBucket(days);
  return key ? (AGEING_BUCKETS.find((b) => b.key === key)!.tone as AgeingTone) : null;
}

export function ageingLabel(key: AgeingBucketKey): string {
  return AGEING_BUCKETS.find((b) => b.key === key)?.label ?? key;
}

const MS_PER_DAY = 86_400_000;

/** Whole days between a billing date and now, floor-ed, never negative. */
const BUSINESS_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });

/** Midnight of the IST business day the instant falls on, as a UTC epoch. */
function startOfBusinessDay(x: Date): number {
  return new Date(`${BUSINESS_DAY.format(x)}T00:00:00.000Z`).getTime();
}

export function daysSince(date: Date | string | null | undefined, now: Date = new Date()): number | null {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return null;
  // Whole IST business days, the same clock the ageing SQL counts by. Counting
  // UTC days here made every chip lag the buckets between midnight and 05:30
  // IST, which is exactly when the night shift reads the yard.
  return Math.max(0, Math.floor((startOfBusinessDay(now) - startOfBusinessDay(d)) / MS_PER_DAY));
}

/** Threshold at which a vehicle becomes an ageing alert. */
export const AGEING_ALERT_DAYS = 60;

/**
 * The lines the ageing screens let you ask about: "how many are standing over N
 * days?" (owner, 25 Sep 2026: "what if i want to know how many vehicles are
 * standing over 90 days for each mitra/outlet/store"). Each is a band edge, so
 * a count over it is a sum of whole bands and agrees with every band list. 60
 * is the default — past it a vehicle is losing money.
 */
export const AGEING_THRESHOLDS = [30, 60, 90, 180, 365] as const;
export type AgeingThreshold = (typeof AGEING_THRESHOLDS)[number];

/** Who holds a vehicle, in the four words the ageing screens use. */
export type AgeingHolderKind = 'STORE' | 'OUTLET' | 'MITRA' | 'OTHER' | 'UNPLACED';

/** One holder's standing stock and its age (`GET /analytics/ageing/holders`). */
export interface AgeingHolderRow {
  /** Null for vehicles no holder is recorded against. */
  partyId: string | null;
  name: string | null;
  kind: AgeingHolderKind;
  /** The holder's recorded area (`Party.city`). */
  area: string | null;
  stock: number;
  /** With a date to count from; `stock - aged` cannot be aged. */
  aged: number;
  /** Standing more than `over` days. */
  over: number;
  oldestDays: number | null;
  /** Count per ageing band, keyed by `AGEING_BUCKETS[].key`. */
  bands: Record<string, number>;
}

export interface AgeingHolderKindTotal {
  kind: AgeingHolderKind;
  /** Holders of this kind with any standing stock. */
  holders: number;
  /** …and how many of them hold something past the line. */
  holdersOver: number;
  stock: number;
  aged: number;
  over: number;
  bands: Record<string, number>;
}

export interface AgeingHoldersResponse {
  over: number;
  asOf: string;
  totals: AgeingHolderKindTotal[];
  data: AgeingHolderRow[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}
